import { PlanningEntityIdentifier, PlanningEntityType } from 'generated';

import { Schedule } from '../../types';

import { Box, SxProps, styled, Tooltip } from '@mui/material';

import { InnerList, ScheduleTooltipBody } from '..';
import ActivitySummary from '../../CardsView/ActivitySummary/ActivitySummary';
import StickyHeader from '../../CardsView/StickyHeader/StickyHeader';
import { panelThemeColors, PanelTheme } from './constants';
import useCopyMode from '../../hooks/useCopyMode';
import { useState } from 'react';

const Panel = styled(Box)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: initial;
  contain: layout;
`;

interface ActivityPanelProps extends Schedule {
  id?: string;
  activeEntityContainingSelection: string | null;
  droppableId?: string;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier?: PlanningEntityIdentifier;
  readOnly?: boolean;
  onConnectClick?: () => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  onChangeActiveEntity?: (entityId: string) => void;
  showEmployeeName?: boolean;
  showStickyTitle?: boolean;
  showDuty?: boolean;
  showDurationDistanceTransport?: boolean;
  sx?: SxProps;
}

function ActivityPanel(props: ActivityPanelProps) {
  const {
    id,
    activeEntityContainingSelection,
    dayPart = 'MORNING',
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    appointmentGrouping,
    name,
    date,
    start,
    end,
    metrics: { productivity, travelKilometers },
    transportationMode,
    durationInSeconds,
    hasDuty,
    onConnectClick,
    onChangeSelection,
    onChangeActiveEntity,
    readOnly,
    events,
    employee,
    employeeType,
    showEmployeeName,
    showStickyTitle = true,
    showDuty = true,
    showDurationDistanceTransport = true,
    startsTooEarly = false,
    endsTooLate = false,
    isLoading,
    isAssignableToEmployee = false,
    sx
  } = props;

  const { isCopyMode } = useCopyMode();
  const [hovered, setHovered] = useState<boolean>(false);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);

  const { borderColor, backgroundColor } =
    panelThemeColors[dayPart as PanelTheme];

  const allowToolTip: boolean =
    hovered && !dialogIsOpen && !isCopyMode && !contextMenuOpen;

  return (
    <Panel
      sx={{
        backgroundColor,
        borderColor,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        ...(sx || {})
      }}
      className={`__${appointmentGrouping?.entityId}__`}
    >
      <Tooltip
        arrow
        enterNextDelay={500}
        disableHoverListener={!allowToolTip}
        title={
          allowToolTip && (
            <ScheduleTooltipBody
              employeeTypeName={employeeType?.name}
              employeeName={employee?.nameInformal}
              appointmentGrouping={appointmentGrouping}
              planningFeatures={employee?.planningFeatures}
              onMouseOver={() => setHovered?.(false)}
            />
          )
        }
      >
        <Box
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {showStickyTitle && (
            <StickyHeader
              id={appointmentGrouping?.entityId ?? planningEntityId}
              droppable={{
                droppableId: id,
                planningEntityType,
                planningEntityId,
                planningEntityIdentifier,
                scheduleStart: start
              }}
              name={name}
              date={date}
              start={start}
              end={end}
              readOnly={readOnly}
              employeeId={employee?.id}
              onConnectClick={onConnectClick}
              onDialogOpen={(isOpen: boolean) => setDialogIsOpen(isOpen)}
              transportationMode={transportationMode}
              events={events}
              startsTooEarly={startsTooEarly}
              endsTooLate={endsTooLate}
              isLoading={isLoading}
              onContextMenu={(open: boolean) => setContextMenuOpen(open)}
              isAssignableToEmployee={isAssignableToEmployee}
            />
          )}
          <ActivitySummary
            planningEntityType={planningEntityType}
            productivity={productivity}
            transportationMode={transportationMode}
            travelKilometers={travelKilometers}
            employee={employee}
            hasDuty={hasDuty}
            durationInSeconds={durationInSeconds}
            employeeType={employeeType}
            showEmployeeName={showEmployeeName}
            showDuty={showDuty}
            showDurationDistanceTransport={showDurationDistanceTransport}
          />
        </Box>
      </Tooltip>
      <InnerList
        activeEntityContainingSelection={activeEntityContainingSelection}
        dayPart={dayPart}
        onChangeSelection={onChangeSelection}
        onChangeActiveEntity={onChangeActiveEntity}
        planningEntityType={planningEntityType}
        planningEntityId={planningEntityId}
        planningEntityIdentifier={planningEntityIdentifier}
        transportationMode={transportationMode}
        scheduleStart={start}
        scheduleId={id}
        events={events}
        readOnly={readOnly}
      />
    </Panel>
  );
}

export default ActivityPanel;
